import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { MagnifyingGlassIcon, PencilSquareIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Modal } from '../../components/ui/Modal';
import { Pagination } from '../../components/ui/Pagination';
import {
  badgeClass,
  DetailCard,
  DetailItem,
  Field,
  FormActions,
  formatMoney,
  formatNumber,
  getApiErrorMessage,
  inputClass,
  inputErrorClass,
  isQuantity,
  primaryButtonClass,
  QUANTITY_STEP,
  secondaryButtonClass,
} from '../../components/ui/FormControls';
import { useProductsPage } from '../../hooks/useInventory';
import { DecideInput, InspectionLot, InspectionParameter, InspectionStatus, ParameterType, qualityService } from '../../services/quality.service';
import { usePermissions } from '../../hooks/usePermissions';
import { cn } from '../../lib/utils';
import { defaultRowsPerPage } from '../../lib/preferences';

const PAGE_SIZES = [10, 25, 50, 100];
const thClass = 'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500';
const round4 = (value: number) => Math.round(value * 10000) / 10000;

const STATUS: Record<InspectionStatus, { label: string; className: string }> = {
  pending: { label: 'Waiting', className: 'bg-amber-50 text-amber-800 ring-amber-600/20' },
  accepted: { label: 'Accepted', className: 'bg-green-50 text-green-700 ring-green-600/20' },
  partially_accepted: { label: 'Partly accepted', className: 'bg-blue-50 text-blue-700 ring-blue-600/20' },
  rejected: { label: 'Rejected', className: 'bg-red-50 text-red-700 ring-red-600/20' },
  cancelled: { label: 'Cancelled', className: 'bg-gray-50 text-gray-600 ring-gray-500/20' },
};
const StatusBadge: React.FC<{ status: InspectionStatus }> = ({ status }) => <span className={cn(badgeClass, STATUS[status]?.className)}>{STATUS[status]?.label ?? status}</span>;

const formatDate = (value?: string | null) =>
  value ? new Date(String(value).length <= 10 ? `${value}T00:00:00` : value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const limitsOf = (parameter: InspectionParameter) => {
  if (parameter.type === 'attribute') return 'Pass / fail';
  const unit = parameter.unit ? ` ${parameter.unit}` : '';
  if (parameter.lowerLimit !== null && parameter.upperLimit !== null) return `${formatNumber(Number(parameter.lowerLimit))} to ${formatNumber(Number(parameter.upperLimit))}${unit}`;
  if (parameter.lowerLimit !== null) return `At least ${formatNumber(Number(parameter.lowerLimit))}${unit}`;
  if (parameter.upperLimit !== null) return `At most ${formatNumber(Number(parameter.upperLimit))}${unit}`;
  return 'No limits set';
};

const useInvalidateQuality = () => {
  const queryClient = useQueryClient();
  return () => ['quality', 'goodsReceipts', 'warehouses', 'products', 'payables', 'purchaseOrders'].forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }));
};

// ---------------------------------------------------------------- one lot: inspect it, or read what was found

interface ResultState {
  measuredValue: string;
  passed: '' | 'pass' | 'fail';
  remarks: string;
  instrumentId: string;
}

const LotPanel: React.FC<{ lotId: string; canInspect: boolean; onClose: () => void }> = ({ lotId, canInspect, onClose }) => {
  const invalidate = useInvalidateQuality();
  const { data: lot, isLoading, isError, error } = useQuery({ queryKey: ['quality', 'lot', lotId], queryFn: () => qualityService.getLot(lotId) });
  const decide = useMutation({ mutationFn: (data: DecideInput) => qualityService.decide(lotId, data), onSuccess: invalidate });
  // Only instruments in service and within their calibration are offered
  const { data: instruments = [] } = useQuery({ queryKey: ['quality', 'usable-instruments'], queryFn: () => qualityService.getUsableInstruments(), enabled: lot?.status === 'pending' });

  const [results, setResults] = useState<Record<string, ResultState>>({});
  const [rejected, setRejected] = useState('0');
  const [reason, setReason] = useState('');
  const [remarks, setRemarks] = useState('');
  const [inspector, setInspector] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  if (isLoading) return <p className="py-10 text-center text-sm text-gray-500">Loading…</p>;
  if (isError || !lot) return <p className="py-10 text-center text-sm text-gray-700">{getApiErrorMessage(error, 'The inspection lot could not be loaded.')}</p>;

  const pending = lot.status === 'pending';
  const parameters = lot.parameters ?? [];
  const resultOf = (id: string): ResultState => results[id] ?? { measuredValue: '', passed: '', remarks: '', instrumentId: '' };
  const setResult = (id: string, field: keyof ResultState, value: string) => setResults((prev) => ({ ...prev, [id]: { ...resultOf(id), [field]: value } }));
  // Within the limits of the plan, for a measurement that has been entered
  const withinLimits = (parameter: InspectionParameter, value: string): boolean | null => {
    if (parameter.type === 'attribute' || value.trim() === '' || !Number.isFinite(Number(value))) return null;
    const measured = Number(value);
    return (parameter.lowerLimit === null || measured >= Number(parameter.lowerLimit)) && (parameter.upperLimit === null || measured <= Number(parameter.upperLimit));
  };
  const rejectedQuantity = Number(rejected) || 0;
  const acceptedQuantity = round4(lot.quantity - rejectedQuantity);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const next: Record<string, string> = {};
    if (!isQuantity(rejectedQuantity) || rejectedQuantity < 0 || rejectedQuantity > lot.quantity) next.rejected = `Enter 0 to ${formatNumber(lot.quantity)}`;
    if (rejectedQuantity > 0 && reason.trim().length < 3) next.reason = 'Say why it is rejected';
    const sent: NonNullable<DecideInput['results']> = [];
    parameters.forEach((parameter) => {
      const state = resultOf(parameter.id as string);
      const measured = state.measuredValue.trim() === '' ? null : Number(state.measuredValue);
      if (parameter.type === 'measurement' && measured === null && state.passed === '') return; // not checked on this lot
      if (parameter.type === 'attribute' && state.passed === '') return;
      if (measured !== null && !Number.isFinite(measured)) next[parameter.id as string] = 'Enter a number';
      sent.push({ parameterId: parameter.id as string, measuredValue: measured, passed: state.passed === '' ? undefined : state.passed === 'pass', remarks: state.remarks.trim() || null, instrumentId: state.instrumentId || null });
    });
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    decide.mutate(
      { acceptedQuantity, rejectedQuantity, rejectionReason: rejectedQuantity > 0 ? reason.trim() : null, remarks: remarks.trim() || null, inspectedByName: inspector.trim() || null, results: sent },
      {
        onSuccess: (saved) => toast.success(`${saved.lotNumber}: ${formatNumber(saved.acceptedQuantity)} accepted${saved.rejectedQuantity > 0 ? `, ${formatNumber(saved.rejectedQuantity)} rejected` : ''}`),
        onError: (submitError) => toast.error(getApiErrorMessage(submitError, 'The inspection could not be saved')),
      },
    );
  };

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-5">
      <DetailCard title="Inspection lot" columns={4}>
        <DetailItem label="Product">{lot.productName}<span className="block text-xs font-normal text-gray-500">{lot.sku}</span></DetailItem>
        <DetailItem label="Quantity">{formatNumber(lot.quantity)} {lot.unit}</DetailItem>
        <DetailItem label="Status"><StatusBadge status={lot.status} /></DetailItem>
        <DetailItem label="Goods receipt">{lot.grnNumber || '—'}<span className="block text-xs font-normal text-gray-500">{formatDate(lot.receiptDate)}</span></DetailItem>
        <DetailItem label="Vendor">{lot.vendorName || '—'}</DetailItem>
        <DetailItem label="Purchase order">{lot.purchaseOrderNumber || '—'}</DetailItem>
        <DetailItem label="Batch / heat">{[lot.batchNumber, lot.heatNumber].filter(Boolean).join(' · ') || '—'}</DetailItem>
        <DetailItem label={pending ? 'Held in' : 'Accepted into'}>{pending ? lot.holdWarehouseName : lot.targetWarehouseName}</DetailItem>
        {!pending && lot.status !== 'cancelled' && (
          <>
            <DetailItem label="Accepted">{formatNumber(lot.acceptedQuantity)}</DetailItem>
            <DetailItem label="Rejected"><span className={lot.rejectedQuantity > 0 ? 'text-red-600' : undefined}>{formatNumber(lot.rejectedQuantity)}</span></DetailItem>
            <DetailItem label="Inspected by">{lot.inspectedByName || '—'}</DetailItem>
            <DetailItem label="Inspected on">{formatDate(lot.inspectedAt)}</DetailItem>
            {lot.rejectionReason && <DetailItem label="Rejected because" className="col-span-2"><span className="font-normal text-red-700">{lot.rejectionReason}</span></DetailItem>}
          </>
        )}
        {lot.remarks && <DetailItem label="Remarks" className="col-span-2"><span className="font-normal">{lot.remarks}</span></DetailItem>}
      </DetailCard>

      {pending ? (
        <>
          <section className="rounded-lg border border-gray-200 bg-white">
            <div className="rounded-t-lg border-b border-gray-200 bg-gray-50 px-4 py-3">
              <h4 className="text-sm font-semibold text-gray-900">Checks</h4>
              <p className="mt-0.5 text-xs text-gray-500">From the inspection plan of the product. A measurement is judged against its limits; leave a check empty if it was not done.</p>
            </div>
            {parameters.length === 0 ? (
              <p className="px-4 py-5 text-sm text-gray-500">This product has no checks on its inspection plan. Decide the lot below.</p>
            ) : (
              <div className="divide-y divide-gray-100">
                {parameters.map((parameter) => {
                  const state = resultOf(parameter.id as string);
                  const within = withinLimits(parameter, state.measuredValue);
                  const verdict = state.passed !== '' ? state.passed === 'pass' : within;
                  return (
                    <div key={parameter.id} className="grid grid-cols-2 gap-3 p-4 lg:grid-cols-12">
                      <div className="col-span-2 lg:col-span-3">
                        <p className="text-sm font-medium text-gray-900">{parameter.name}</p>
                        <p className="text-xs text-gray-500">{[limitsOf(parameter), parameter.method].filter(Boolean).join(' · ')}</p>
                        {errors[parameter.id as string] && <p className="mt-1 text-xs text-red-600">{errors[parameter.id as string]}</p>}
                      </div>
                      {parameter.type === 'measurement' ? (
                        <Field label="Measured" htmlFor={`m-${parameter.id}`} className="lg:col-span-2">
                          <input id={`m-${parameter.id}`} type="number" step="0.0001" disabled={!canInspect} value={state.measuredValue} onChange={(event) => setResult(parameter.id as string, 'measuredValue', event.target.value)} aria-label={`Measured value of ${parameter.name}`} className={cn(inputClass, 'text-right tabular-nums', within === false && state.passed === '' && inputErrorClass)} />
                        </Field>
                      ) : (
                        <div className="hidden lg:col-span-2 lg:block" />
                      )}
                      <Field label="Instrument" htmlFor={`i-${parameter.id}`} className="lg:col-span-2">
                        <select id={`i-${parameter.id}`} disabled={!canInspect} value={state.instrumentId} onChange={(event) => setResult(parameter.id as string, 'instrumentId', event.target.value)} aria-label={`Instrument for ${parameter.name}`} className={inputClass}>
                          <option value="">Not recorded</option>
                          {instruments.map((instrument) => (<option key={instrument.id} value={instrument.id}>{instrument.code} · {instrument.name}</option>))}
                        </select>
                      </Field>
                      <Field label="Result" htmlFor={`p-${parameter.id}`} className="lg:col-span-2">
                        <select id={`p-${parameter.id}`} disabled={!canInspect} value={state.passed} onChange={(event) => setResult(parameter.id as string, 'passed', event.target.value)} aria-label={`Result of ${parameter.name}`} className={inputClass}>
                          <option value="">{parameter.type === 'measurement' ? 'By the limits' : 'Not checked'}</option>
                          <option value="pass">Pass</option>
                          <option value="fail">Fail</option>
                        </select>
                      </Field>
                      <Field label="Remarks" htmlFor={`r-${parameter.id}`} className="col-span-2 lg:col-span-2">
                        <input id={`r-${parameter.id}`} type="text" maxLength={300} disabled={!canInspect} value={state.remarks} onChange={(event) => setResult(parameter.id as string, 'remarks', event.target.value)} className={inputClass} />
                      </Field>
                      <div className="col-span-2 flex items-end lg:col-span-1">
                        {verdict !== null && <span className={cn(badgeClass, verdict ? 'bg-green-50 text-green-700 ring-green-600/20' : 'bg-red-50 text-red-700 ring-red-600/20')}>{verdict ? 'Pass' : 'Fail'}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {canInspect && (
            <section className="rounded-lg border border-gray-200 bg-white">
              <div className="rounded-t-lg border-b border-gray-200 bg-gray-50 px-4 py-3">
                <h4 className="text-sm font-semibold text-gray-900">Decision</h4>
                <p className="mt-0.5 text-xs text-gray-500">Accepted goods move to {lot.targetWarehouseName}. Rejected goods leave stock and the purchase order expects them again.</p>
              </div>
              <div className="grid grid-cols-2 gap-3 p-4 lg:grid-cols-4">
                <Field label="Rejected" htmlFor="il-rejected" error={errors.rejected}>
                  <input id="il-rejected" type="number" min="0" max={lot.quantity} step={QUANTITY_STEP} value={rejected} onChange={(event) => setRejected(event.target.value)} className={cn(inputClass, 'text-right tabular-nums', errors.rejected && inputErrorClass)} />
                </Field>
                <Field label="Accepted">
                  <p className="rounded-md bg-gray-50 px-3 py-2 text-right text-sm font-semibold text-gray-900 tabular-nums ring-1 ring-gray-200">{formatNumber(Math.max(0, acceptedQuantity))} {lot.unit}</p>
                </Field>
                <Field label="Inspected by" htmlFor="il-inspector" className="col-span-2">
                  <input id="il-inspector" type="text" maxLength={100} value={inspector} onChange={(event) => setInspector(event.target.value)} className={inputClass} />
                </Field>
                {rejectedQuantity > 0 && (
                  <Field label="Reason for rejection" htmlFor="il-reason" required error={errors.reason} className="col-span-2 lg:col-span-4">
                    <input id="il-reason" type="text" maxLength={300} value={reason} onChange={(event) => setReason(event.target.value)} className={cn(inputClass, errors.reason && inputErrorClass)} />
                  </Field>
                )}
                <Field label="Remarks" htmlFor="il-remarks" className="col-span-2 lg:col-span-4">
                  <input id="il-remarks" type="text" maxLength={500} value={remarks} onChange={(event) => setRemarks(event.target.value)} className={inputClass} />
                </Field>
              </div>
            </section>
          )}
        </>
      ) : (
        (lot.results ?? []).length > 0 && (
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className={thClass}>Check</th>
                  <th scope="col" className={thClass}>Limits</th>
                  <th scope="col" className={cn(thClass, 'text-right')}>Measured</th>
                  <th scope="col" className={thClass}>Instrument</th>
                  <th scope="col" className={thClass}>Result</th>
                  <th scope="col" className={thClass}>Remarks</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(lot.results ?? []).map((result) => (
                  <tr key={result.id}>
                    <td className="px-4 py-2.5 text-sm font-medium text-gray-900">{result.name}</td>
                    <td className="px-4 py-2.5 text-sm text-gray-600">{limitsOf(result)}</td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-right text-sm text-gray-900 tabular-nums">{result.measuredValue === null ? '—' : `${formatNumber(Number(result.measuredValue))} ${result.unit ?? ''}`}</td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-sm text-gray-600">{result.instrumentCode || '—'}</td>
                    <td className="px-4 py-2.5"><span className={cn(badgeClass, result.passed ? 'bg-green-50 text-green-700 ring-green-600/20' : 'bg-red-50 text-red-700 ring-red-600/20')}>{result.passed ? 'Pass' : 'Fail'}</span></td>
                    <td className="px-4 py-2.5 text-sm text-gray-600">{result.remarks || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      <FormActions>
        <button type="button" onClick={onClose} disabled={decide.isPending} className={secondaryButtonClass}>Close</button>
        {!pending && lot.rejectedQuantity > 0 && (
          <Link to={`/ncrs?lot=${lot.id}`} className={secondaryButtonClass}>
            Raise NCR
          </Link>
        )}
        {pending && canInspect && (
          <button type="submit" disabled={decide.isPending} className={primaryButtonClass}>{decide.isPending ? 'Saving…' : 'Record Inspection'}</button>
        )}
      </FormActions>
    </form>
  );
};

// ---------------------------------------------------------------- inspection plan of a product

interface ParameterState {
  key: string;
  sequence: string;
  name: string;
  type: ParameterType;
  method: string;
  unit: string;
  nominal: string;
  lowerLimit: string;
  upperLimit: string;
}
let parameterKey = 0;
const text = (value: number | null) => (value === null || value === undefined ? '' : String(value));

const PlanEditor: React.FC<{ productId: string; canEdit: boolean; onClose: () => void }> = ({ productId, canEdit, onClose }) => {
  const invalidate = useInvalidateQuality();
  const { data: plan, isLoading } = useQuery({ queryKey: ['quality', 'plan', productId], queryFn: () => qualityService.getPlan(productId) });
  const savePlan = useMutation({ mutationFn: (data: Parameters<typeof qualityService.savePlan>[1]) => qualityService.savePlan(productId, data), onSuccess: invalidate });
  const [required, setRequired] = useState(true);
  const [rows, setRows] = useState<ParameterState[] | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (plan && rows === null) {
      setRequired(plan.parameters.length === 0 && !plan.requiresInspection ? true : plan.requiresInspection);
      setRows(plan.parameters.map((parameter) => ({ key: `ip-${parameterKey++}`, sequence: String(parameter.sequence), name: parameter.name, type: parameter.type, method: parameter.method ?? '', unit: parameter.unit ?? '', nominal: text(parameter.nominal), lowerLimit: text(parameter.lowerLimit), upperLimit: text(parameter.upperLimit) })));
    }
  }, [plan, rows]);

  if (isLoading || rows === null) return <p className="py-10 text-center text-sm text-gray-500">Loading…</p>;
  const setRow = (key: string, field: keyof ParameterState, value: string) => {
    setRows((prev) => (prev ?? []).map((row) => (row.key === key ? { ...row, [field]: value } : row)));
    setErrors((prev) => {
      const { [key]: _cleared, ...rest } = prev;
      return rest;
    });
  };
  const number = (value: string) => (value.trim() === '' ? null : Number(value));

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const next: Record<string, string> = {};
    const seen = new Set<number>();
    const parameters = rows.map((row) => {
      const sequence = Number(row.sequence);
      const lower = number(row.lowerLimit);
      const upper = number(row.upperLimit);
      if (!Number.isInteger(sequence) || sequence < 1) next[row.key] = 'The sequence is a whole number, 1 or more';
      else if (seen.has(sequence)) next[row.key] = `Sequence ${sequence} is used twice`;
      else if (row.name.trim().length < 2) next[row.key] = 'Name what is checked';
      else if ([number(row.nominal), lower, upper].some((value) => value !== null && !Number.isFinite(value))) next[row.key] = 'Limits must be numbers';
      else if (lower !== null && upper !== null && lower > upper) next[row.key] = 'The lower limit is above the upper limit';
      seen.add(sequence);
      return { sequence, name: row.name.trim(), type: row.type, method: row.method.trim() || null, unit: row.unit.trim() || null, nominal: row.type === 'attribute' ? null : number(row.nominal), lowerLimit: row.type === 'attribute' ? null : lower, upperLimit: row.type === 'attribute' ? null : upper };
    });
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    savePlan.mutate(
      { requiresInspection: required, parameters },
      {
        onSuccess: (saved) => { toast.success(`Inspection plan of ${saved.productName} saved`); onClose(); },
        onError: (submitError) => toast.error(getApiErrorMessage(submitError, 'The inspection plan could not be saved')),
      },
    );
  };

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-5">
      <div className="rounded-lg bg-gray-50 p-4 ring-1 ring-gray-200">
        <p className="text-sm font-semibold text-gray-900">{plan?.productName}</p>
        <p className="text-xs text-gray-500">{plan?.sku}</p>
      </div>
      <label className="flex items-start gap-3">
        <input type="checkbox" checked={required} disabled={!canEdit} onChange={(event) => setRequired(event.target.checked)} className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
        <span>
          <span className="block text-sm font-medium text-gray-900">Hold for inspection when received</span>
          <span className="block text-xs text-gray-500">Goods of this product go into the inspection hold on every goods receipt and cannot be used, sold or billed until they are inspected.</span>
        </span>
      </label>

      <div className="space-y-3">
        {rows.map((row) => (
          <div key={row.key} className={cn('rounded-lg border bg-white p-4', errors[row.key] ? 'border-red-300' : 'border-gray-200')}>
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-12">
              <Field label="Seq." htmlFor={`${row.key}-s`} className="lg:col-span-1"><input id={`${row.key}-s`} type="number" min="1" step="1" disabled={!canEdit} value={row.sequence} onChange={(event) => setRow(row.key, 'sequence', event.target.value)} className={cn(inputClass, 'tabular-nums')} /></Field>
              <Field label="Check" htmlFor={`${row.key}-n`} required className="col-span-2 lg:col-span-3"><input id={`${row.key}-n`} type="text" maxLength={100} disabled={!canEdit} value={row.name} onChange={(event) => setRow(row.key, 'name', event.target.value)} aria-label="Check name" className={inputClass} placeholder="For example outer diameter" /></Field>
              <Field label="Kind" htmlFor={`${row.key}-t`} className="lg:col-span-2">
                <select id={`${row.key}-t`} disabled={!canEdit} value={row.type} onChange={(event) => setRow(row.key, 'type', event.target.value)} className={inputClass}>
                  <option value="measurement">Measured value</option>
                  <option value="attribute">Pass / fail</option>
                </select>
              </Field>
              <Field label="Method" htmlFor={`${row.key}-m`} className="lg:col-span-2"><input id={`${row.key}-m`} type="text" maxLength={100} disabled={!canEdit} value={row.method} onChange={(event) => setRow(row.key, 'method', event.target.value)} className={inputClass} placeholder="Micrometer" /></Field>
              {row.type === 'measurement' ? (
                <>
                  <Field label="Unit" htmlFor={`${row.key}-u`} className="lg:col-span-1"><input id={`${row.key}-u`} type="text" maxLength={20} disabled={!canEdit} value={row.unit} onChange={(event) => setRow(row.key, 'unit', event.target.value)} className={inputClass} placeholder="mm" /></Field>
                  <Field label="Lower" htmlFor={`${row.key}-l`} className="lg:col-span-1"><input id={`${row.key}-l`} type="number" step="0.0001" disabled={!canEdit} value={row.lowerLimit} onChange={(event) => setRow(row.key, 'lowerLimit', event.target.value)} aria-label="Lower limit" className={cn(inputClass, 'text-right tabular-nums')} /></Field>
                  <Field label="Nominal" htmlFor={`${row.key}-o`} className="lg:col-span-1"><input id={`${row.key}-o`} type="number" step="0.0001" disabled={!canEdit} value={row.nominal} onChange={(event) => setRow(row.key, 'nominal', event.target.value)} className={cn(inputClass, 'text-right tabular-nums')} /></Field>
                  <Field label="Upper" htmlFor={`${row.key}-h`} className="lg:col-span-1"><input id={`${row.key}-h`} type="number" step="0.0001" disabled={!canEdit} value={row.upperLimit} onChange={(event) => setRow(row.key, 'upperLimit', event.target.value)} aria-label="Upper limit" className={cn(inputClass, 'text-right tabular-nums')} /></Field>
                </>
              ) : (
                <div className="hidden lg:col-span-4 lg:block" />
              )}
            </div>
            <div className="mt-2 flex items-center justify-between">
              <p className="text-sm text-red-600">{errors[row.key]}</p>
              {canEdit && (
                <button type="button" onClick={() => setRows((prev) => (prev ?? []).filter((candidate) => candidate.key !== row.key))} className="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium text-gray-500 hover:bg-red-50 hover:text-red-600">
                  <XMarkIcon className="mr-1 h-3.5 w-3.5" />
                  Remove
                </button>
              )}
            </div>
          </div>
        ))}
        {canEdit && (
          <button type="button" onClick={() => setRows((prev) => [...(prev ?? []), { key: `ip-${parameterKey++}`, sequence: String(Math.max(0, ...(prev ?? []).map((row) => Number(row.sequence) || 0)) + 10), name: '', type: 'measurement', method: '', unit: '', nominal: '', lowerLimit: '', upperLimit: '' }])} className={secondaryButtonClass}>
            <PlusIcon className="-ml-0.5 mr-2 h-4 w-4" />
            Add Check
          </button>
        )}
      </div>

      <FormActions>
        <button type="button" onClick={onClose} disabled={savePlan.isPending} className={secondaryButtonClass}>{canEdit ? 'Cancel' : 'Close'}</button>
        {canEdit && <button type="submit" disabled={savePlan.isPending} className={primaryButtonClass}>{savePlan.isPending ? 'Saving…' : 'Save Plan'}</button>}
      </FormActions>
    </form>
  );
};

const ProductPicker: React.FC<{ onPick: (productId: string) => void }> = ({ onPick }) => {
  const [input, setInput] = useState('');
  const [search, setSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setSearch(input.trim()), 300);
    return () => clearTimeout(timer);
  }, [input]);
  const { data, isFetching } = useProductsPage({ page: 1, limit: 8, search: search || undefined });
  const products = data?.data ?? [];
  return (
    <div className="space-y-3">
      <div className="relative">
        <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <input id="plan-product-search" type="search" autoFocus value={input} onChange={(event) => setInput(event.target.value)} placeholder="Search the product to inspect" aria-label="Search products" className={cn(inputClass, 'pl-9')} />
      </div>
      <ul className={cn('divide-y divide-gray-100 rounded-md border border-gray-200', isFetching && 'opacity-60')}>
        {products.length === 0 ? (
          <li className="px-3 py-3 text-sm text-gray-500">{data ? 'No product matches.' : 'Loading…'}</li>
        ) : (
          products.map((product) => (
            <li key={product.id}>
              <button type="button" onClick={() => onPick(product.id)} className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left hover:bg-indigo-50">
                <span className="min-w-0"><span className="block truncate text-sm font-medium text-gray-900">{product.name}</span><span className="block text-xs text-gray-500">{product.sku}</span></span>
                <span className="shrink-0 text-xs font-semibold text-indigo-600">Choose</span>
              </button>
            </li>
          ))
        )}
      </ul>
    </div>
  );
};

// ---------------------------------------------------------------- page

type Tab = 'lots' | 'plans';

const Inspections: React.FC = () => {
  const { can } = usePermissions();
  const canInspect = can('quality.update');

  const [tab, setTab] = useState<Tab>('lots');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<InspectionStatus | ''>('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(defaultRowsPerPage);
  const [viewing, setViewing] = useState<InspectionLot | null>(null);
  const [picking, setPicking] = useState(false);
  const [planProductId, setPlanProductId] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const lots = useQuery({ queryKey: ['quality', 'lots', { page, limit, search, statusFilter }], queryFn: () => qualityService.getLots({ page, limit, search: search || undefined, status: statusFilter }), placeholderData: keepPreviousData, enabled: tab === 'lots' });
  const plans = useQuery({ queryKey: ['quality', 'plans', search], queryFn: () => qualityService.getPlans(search || undefined), enabled: tab === 'plans' });
  const { data: summary } = useQuery({ queryKey: ['quality', 'summary'], queryFn: () => qualityService.getSummary() });

  const total = lots.data?.total ?? 0;
  const totalPages = lots.data?.totalPages ?? 1;
  useEffect(() => {
    if (lots.data && page > lots.data.totalPages) setPage(lots.data.totalPages);
  }, [lots.data, page]);

  const stats = [
    { label: 'Waiting for inspection', value: summary ? formatNumber(summary.pending) : '—', hint: summary && summary.pending > 0 ? `Oldest has waited ${summary.oldestDays} day(s)` : 'Nothing is held' },
    { label: 'Value held', value: summary ? formatMoney(summary.heldValue) : '—', hint: 'In the inspection hold, at purchase price' },
    { label: 'Inspected this month', value: summary ? formatNumber(summary.inspectedThisMonth) : '—', hint: 'Lots decided' },
    { label: 'Rejected this month', value: summary ? `${formatNumber(summary.rejectionRate, 1)}%` : '—', hint: `${summary ? formatNumber(summary.rejectedThisMonth) : '—'} unit(s) of what was inspected` },
  ];
  const firstRow = total > 0 ? (page - 1) * limit + 1 : 0;
  const lastRow = Math.min(page * limit, total);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Incoming Inspection</h1>
          <p className="mt-1 text-sm text-gray-500">Goods of inspected products wait in the inspection hold until they are checked. Accepted goods go to the store; rejected goods go back onto the purchase order.</p>
        </div>
        {canInspect && tab === 'plans' && (
          <button type="button" onClick={() => setPicking(true)} className={primaryButtonClass}>
            <PlusIcon className="-ml-0.5 mr-2 h-4 w-4" />
            Add Product to Inspect
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {stats.map((stat) => (
          <div key={stat.label} className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{stat.label}</p>
            <p className="mt-1 truncate text-2xl font-semibold text-gray-900">{stat.value}</p>
            <p className="mt-1 text-xs text-gray-500">{stat.hint}</p>
          </div>
        ))}
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
        <div className="flex gap-1 border-b border-gray-200 px-4 pt-3" role="tablist">
          {([{ key: 'lots', label: 'Inspection lots' }, { key: 'plans', label: 'Inspection plans' }] as { key: Tab; label: string }[]).map((item) => (
            <button key={item.key} type="button" role="tab" aria-selected={tab === item.key} onClick={() => { setTab(item.key); setPage(1); setSearchInput(''); setSearch(''); }} className={cn('-mb-px border-b-2 px-3 py-2 text-sm font-medium', tab === item.key ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700')}>
              {item.label}
            </button>
          ))}
        </div>
        <div className="flex flex-col gap-3 border-b border-gray-200 p-4 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input type="search" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder={tab === 'lots' ? 'Search by lot, product, GRN, vendor, batch or heat number' : 'Search by product or SKU'} aria-label="Search" className={cn(inputClass, 'pl-9')} />
          </div>
          {tab === 'lots' && (
            <select value={statusFilter} onChange={(event) => { setStatusFilter(event.target.value as InspectionStatus | ''); setPage(1); }} aria-label="Status" className={cn(inputClass, 'lg:w-48')}>
              <option value="">All statuses</option>
              {(Object.keys(STATUS) as InspectionStatus[]).map((status) => (<option key={status} value={status}>{STATUS[status].label}</option>))}
            </select>
          )}
        </div>

        {tab === 'lots' ? (
          <div className={cn('overflow-x-auto', lots.isFetching && lots.data && 'opacity-60')}>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className={thClass}>Lot</th>
                  <th scope="col" className={thClass}>Product</th>
                  <th scope="col" className={thClass}>Received</th>
                  <th scope="col" className={cn(thClass, 'text-right')}>Quantity</th>
                  <th scope="col" className={cn(thClass, 'text-right')}>Rejected</th>
                  <th scope="col" className={thClass}>Status</th>
                  <th scope="col" className={cn(thClass, 'text-right')}>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {lots.isError ? (
                  <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-700">{getApiErrorMessage(lots.error, 'The inspection lots could not be loaded.')}</td></tr>
                ) : lots.isLoading ? (
                  <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-500">Loading…</td></tr>
                ) : (lots.data?.data ?? []).length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center">
                      <p className="text-sm font-medium text-gray-900">{search || statusFilter ? 'No inspection lot matches' : 'Nothing to inspect yet'}</p>
                      <p className="mt-1 text-sm text-gray-500">A lot appears here when goods of a product marked for inspection are received. Mark products under Inspection plans.</p>
                    </td>
                  </tr>
                ) : (
                  (lots.data?.data ?? []).map((lot) => (
                    <tr key={lot.id} className="hover:bg-gray-50">
                      <td className="whitespace-nowrap px-4 py-3">
                        <button type="button" onClick={() => setViewing(lot)} className="text-sm font-medium text-indigo-600 hover:text-indigo-500">{lot.lotNumber}</button>
                        {lot.status === 'pending' && <p className="mt-0.5 text-xs text-amber-700">Waiting {lot.daysWaiting} day(s)</p>}
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-gray-900">{lot.productName}</p>
                        <p className="mt-0.5 text-xs text-gray-500">{[lot.sku, lot.heatNumber ? `heat ${lot.heatNumber}` : null, lot.batchNumber ? `batch ${lot.batchNumber}` : null].filter(Boolean).join(' · ')}</p>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">{lot.grnNumber || '—'}<span className="block text-xs text-gray-500">{lot.vendorName}</span></td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-medium text-gray-900 tabular-nums">{formatNumber(lot.quantity)} <span className="font-normal text-gray-500">{lot.unit}</span></td>
                      <td className={cn('whitespace-nowrap px-4 py-3 text-right text-sm tabular-nums', lot.rejectedQuantity > 0 ? 'font-medium text-red-600' : 'text-gray-400')}>{lot.rejectedQuantity > 0 ? formatNumber(lot.rejectedQuantity) : '—'}</td>
                      <td className="whitespace-nowrap px-4 py-3"><StatusBadge status={lot.status} /></td>
                      <td className="whitespace-nowrap px-4 py-3 text-right">
                        <button type="button" onClick={() => setViewing(lot)} className="rounded-md px-2.5 py-1 text-xs font-semibold text-indigo-600 ring-1 ring-inset ring-indigo-200 hover:bg-indigo-50" aria-label={`Open ${lot.lotNumber}`}>
                          {lot.status === 'pending' && canInspect ? 'Inspect' : 'View'}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className={thClass}>Product</th>
                  <th scope="col" className={thClass}>On receipt</th>
                  <th scope="col" className={cn(thClass, 'text-right')}>Checks</th>
                  <th scope="col" className={cn(thClass, 'text-right')}>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {plans.isLoading ? (
                  <tr><td colSpan={4} className="px-4 py-10 text-center text-sm text-gray-500">Loading…</td></tr>
                ) : (plans.data ?? []).length === 0 ? (
                  <tr><td colSpan={4} className="px-4 py-12 text-center text-sm text-gray-500">{search ? 'No product matches.' : 'No product is inspected on receipt yet.'}</td></tr>
                ) : (
                  (plans.data ?? []).map((plan) => (
                    <tr key={plan.productId} className="hover:bg-gray-50">
                      <td className="px-4 py-3"><p className="text-sm font-medium text-gray-900">{plan.productName}</p><p className="mt-0.5 text-xs text-gray-500">{plan.sku}</p></td>
                      <td className="px-4 py-3"><span className={cn(badgeClass, plan.requiresInspection ? 'bg-amber-50 text-amber-800 ring-amber-600/20' : 'bg-gray-50 text-gray-600 ring-gray-500/20')}>{plan.requiresInspection ? 'Held for inspection' : 'Not held'}</span></td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700 tabular-nums">{formatNumber(plan.parameters)}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-right">
                        <button type="button" onClick={() => setPlanProductId(plan.productId)} title="Open the plan" aria-label={`Open the plan of ${plan.productName}`} className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"><PencilSquareIcon className="h-4 w-4" /></button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {tab === 'lots' && (
          <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600">
              <span>Showing {firstRow}–{lastRow} of {formatNumber(total)}</span>
              <label className="flex items-center gap-2">
                Rows per page
                <select value={limit} onChange={(event) => { setLimit(Number(event.target.value)); setPage(1); }} className="rounded-md border border-gray-300 bg-white py-1 pl-2 pr-7 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500">
                  {PAGE_SIZES.map((size) => (<option key={size} value={size}>{size}</option>))}
                </select>
              </label>
            </div>
            {totalPages > 1 && <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />}
          </div>
        )}
      </div>

      <Modal isOpen={!!viewing} onClose={() => setViewing(null)} title={viewing?.lotNumber ?? 'Inspection Lot'} size="2xl">
        {viewing && <LotPanel key={viewing.id} lotId={viewing.id} canInspect={canInspect} onClose={() => setViewing(null)} />}
      </Modal>

      {/* One dialog for choosing the product and editing its plan, so the focus stays in the form */}
      <Modal isOpen={picking || !!planProductId} onClose={() => { setPicking(false); setPlanProductId(null); }} title={planProductId ? 'Inspection Plan' : 'Add Product to Inspect'} description={planProductId ? 'What is checked when goods of this product are received.' : 'Choose the product. If it already has a plan, that plan opens.'} size="2xl">
        {planProductId ? (
          <PlanEditor key={planProductId} productId={planProductId} canEdit={canInspect} onClose={() => setPlanProductId(null)} />
        ) : picking ? (
          <ProductPicker onPick={(productId) => { setPicking(false); setPlanProductId(productId); }} />
        ) : null}
      </Modal>
    </div>
  );
};

export default Inspections;
